/**
 * Membership Verification queue.
 *
 * Two adjacent queues for admins and delegates:
 *
 *   1. Pending org-membership claims — contestants who tapped "Save member
 *      number" on the registration page. They are treated as members
 *      across the app until you Approve (locks it in) or Reject (deletes
 *      the row and reverts them to non-member).
 *
 *   2. Per-registration claim verifications (legacy trust path) — claims
 *      stored on individual event_registrations rows from before the
 *      org-membership flow shipped. Approving optionally seeds an active
 *      organization_memberships row; rejecting flips the registration back
 *      to non-member and re-applies the rodeo's non-member surcharge.
 */
import { useMemo, useState } from 'react';
import { BadgeCheck, ShieldAlert, Loader2, RefreshCw, X, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/contexts/ToastContext';
import {
  usePendingMemberVerifications,
  useRejectMembership,
  useVerifyMembership,
  usePendingMembershipClaims,
  useApproveMembershipClaim,
  useRejectMembershipClaim,
  type PendingVerification,
  type PendingMembershipClaim,
} from '@/api/extensions/useMembershipVerificationOps';

export default function MembershipVerificationPage() {
  const { data, isLoading, refetch, isFetching } = usePendingMemberVerifications();
  const verify = useVerifyMembership();
  const reject = useRejectMembership();
  const {
    data: membershipClaims,
    isLoading: claimsLoading,
    refetch: refetchClaims,
    isFetching: claimsFetching,
  } = usePendingMembershipClaims();
  const approveClaim = useApproveMembershipClaim();
  const rejectClaim = useRejectMembershipClaim();
  const { showSuccess, showError } = useToast();

  const [search, setSearch] = useState('');
  const [pendingAction, setPendingAction] = useState<{
    registration_id: string;
    mode: 'verify' | 'reject';
  } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [createMembership, setCreateMembership] = useState(true);

  // Org-membership claim state — separate from the per-registration queue
  // above so admins can act on either independently.
  const [claimSearch, setClaimSearch] = useState('');
  const [pendingClaimAction, setPendingClaimAction] = useState<{
    organization_id: string;
    user_profile_id: string;
    mode: 'approve' | 'reject';
  } | null>(null);
  const [claimRejectReason, setClaimRejectReason] = useState('');

  const filteredClaims = useMemo(() => {
    if (!membershipClaims) return [] as PendingMembershipClaim[];
    if (!claimSearch.trim()) return membershipClaims;
    const q = claimSearch.trim().toLowerCase();
    return membershipClaims.filter((c) =>
      [c.user_display_name, c.user_email, c.organization_name, c.member_number]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(q)),
    );
  }, [membershipClaims, claimSearch]);

  const handleApproveClaim = async (target: PendingMembershipClaim) => {
    try {
      await approveClaim.mutateAsync({
        organizationId: target.organization_id,
        userProfileId: target.user_profile_id,
      });
      showSuccess(
        'Membership approved',
        `${target.user_display_name ?? 'Contestant'} confirmed at ${
          target.organization_name ?? 'this org'
        }.`,
      );
      setPendingClaimAction(null);
    } catch (err: any) {
      showError('Failed to approve', err?.message ?? 'Unknown error');
    }
  };

  const handleRejectClaim = async (target: PendingMembershipClaim) => {
    try {
      await rejectClaim.mutateAsync({
        organizationId: target.organization_id,
        userProfileId: target.user_profile_id,
        reason: claimRejectReason,
      });
      showSuccess(
        'Membership claim rejected',
        `${target.user_display_name ?? 'Contestant'} reverted to non-member at ${
          target.organization_name ?? 'this org'
        }.`,
      );
      setPendingClaimAction(null);
      setClaimRejectReason('');
    } catch (err: any) {
      showError('Failed to reject', err?.message ?? 'Unknown error');
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [] as PendingVerification[];
    if (!search.trim()) return data;
    const q = search.trim().toLowerCase();
    return data.filter((v) =>
      [
        v.user_display_name,
        v.event_name,
        v.rodeo_name,
        v.claimed_member_number,
      ]
        .filter(Boolean)
        .some((s) => s!.toLowerCase().includes(q)),
    );
  }, [data, search]);

  const handleVerify = async (target: PendingVerification) => {
    try {
      await verify.mutateAsync({
        registrationId: target.registration_id,
        createMembership,
      });
      showSuccess(
        'Member verified',
        `${target.user_display_name ?? 'Contestant'} approved for ${
          target.rodeo_name ?? 'the rodeo'
        }.`,
      );
      setPendingAction(null);
    } catch (err: any) {
      showError('Failed to verify', err?.message ?? 'Unknown error');
    }
  };

  const handleReject = async (target: PendingVerification) => {
    try {
      const result = await reject.mutateAsync({
        registrationId: target.registration_id,
        reason: rejectReason,
      });
      showSuccess(
        'Member claim rejected',
        result.non_member_fee > 0
          ? `Non-member fee of $${result.non_member_fee.toFixed(2)} now applies.`
          : 'Entry flipped to non-member.',
      );
      setPendingAction(null);
      setRejectReason('');
    } catch (err: any) {
      showError('Failed to reject', err?.message ?? 'Unknown error');
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-primary" />
            Membership Verification
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Confirm or reject contestants who claimed membership at registration time.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            refetch();
            refetchClaims();
          }}
          disabled={isFetching || claimsFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${
              isFetching || claimsFetching ? 'animate-spin' : ''
            }`}
          />
          Refresh
        </Button>
      </header>

      {/* Org-membership claim queue — drives the new "Save member number"
          flow on the registration modal. Approving locks the user in as a
          member; rejecting deletes the row entirely so they revert to
          non-member status (and pay the surcharge on future registrations). */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Pending member-number claims</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Contestants who saved a member number on the registration page.
            They are treated as members until you act.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <Input
              type="search"
              placeholder="Search name, email, org, member number…"
              className="max-w-sm"
              value={claimSearch}
              onChange={(e) => setClaimSearch(e.target.value)}
            />
            <Badge variant="secondary" className="ml-auto">
              {filteredClaims.length} pending
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {claimsLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : filteredClaims.length === 0 ? (
            <div className="p-12 text-center">
              <BadgeCheck className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                {membershipClaims && membershipClaims.length > 0
                  ? 'No claims match your search.'
                  : 'All caught up — no member-number claims to review.'}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredClaims.map((target) => {
                const key = `${target.organization_id}:${target.user_profile_id}`;
                const isAction =
                  pendingClaimAction?.organization_id === target.organization_id &&
                  pendingClaimAction?.user_profile_id === target.user_profile_id;
                return (
                  <div key={key} className="p-4 space-y-3">
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {target.user_display_name ?? 'Unknown contestant'}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {[target.organization_name, target.user_email]
                            .filter(Boolean)
                            .join(' • ') || 'No org / email info'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">
                          Claimed member #
                        </div>
                        <div className="font-mono text-sm">
                          {target.member_number ?? '—'}
                        </div>
                      </div>
                    </div>

                    {isAction && pendingClaimAction!.mode === 'reject' && (
                      <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                        <Label htmlFor="claim-reject-reason" className="text-xs">
                          Reason (sent to the contestant; optional)
                        </Label>
                        <Input
                          id="claim-reject-reason"
                          value={claimRejectReason}
                          onChange={(e) => setClaimRejectReason(e.target.value)}
                          placeholder="e.g. We can't find that member number on file."
                        />
                        <p className="text-xs text-muted-foreground">
                          Rejecting deletes the membership row. The contestant
                          becomes a non-member again and will pay the
                          non-member surcharge on future registrations.
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 justify-end">
                      {isAction ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPendingClaimAction(null);
                              setClaimRejectReason('');
                            }}
                          >
                            Cancel
                          </Button>
                          {pendingClaimAction!.mode === 'approve' ? (
                            <Button
                              size="sm"
                              disabled={approveClaim.isPending}
                              onClick={() => handleApproveClaim(target)}
                            >
                              {approveClaim.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 mr-2" />
                              )}
                              Confirm approve
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={rejectClaim.isPending}
                              onClick={() => handleRejectClaim(target)}
                            >
                              {rejectClaim.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <X className="h-4 w-4 mr-2" />
                              )}
                              Confirm reject
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPendingClaimAction({
                                organization_id: target.organization_id,
                                user_profile_id: target.user_profile_id,
                                mode: 'reject',
                              })
                            }
                          >
                            <X className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              setPendingClaimAction({
                                organization_id: target.organization_id,
                                user_profile_id: target.user_profile_id,
                                mode: 'approve',
                              })
                            }
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Per-entry verification claims (legacy)</CardTitle>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <Input
              type="search"
              placeholder="Search name, event, rodeo, member number…"
              className="max-w-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <label className="flex items-center gap-2 text-xs text-muted-foreground select-none">
              <input
                type="checkbox"
                className="h-3.5 w-3.5"
                checked={createMembership}
                onChange={(e) => setCreateMembership(e.target.checked)}
              />
              Also add to org member list when verifying
            </label>
            <Badge variant="secondary" className="ml-auto">
              {filtered.length} pending
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <BadgeCheck className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                {data && data.length > 0
                  ? 'No claims match your search.'
                  : "All caught up — nothing to verify right now."}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {filtered.map((target) => {
                const isAction = pendingAction?.registration_id === target.registration_id;
                return (
                  <div key={target.registration_id} className="p-4 space-y-3">
                    <div className="flex flex-wrap items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">
                          {target.user_display_name ?? 'Unknown contestant'}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {[target.rodeo_name, target.event_name].filter(Boolean).join(' • ') ||
                            'No rodeo / event info'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-muted-foreground">Claimed member #</div>
                        <div className="font-mono text-sm">
                          {target.claimed_member_number ?? '—'}
                        </div>
                      </div>
                    </div>

                    {isAction && pendingAction!.mode === 'reject' && (
                      <div className="space-y-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                        <Label htmlFor="reject-reason" className="text-xs">
                          Reason (sent to the contestant; optional)
                        </Label>
                        <Input
                          id="reject-reason"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="e.g. We can't find that member number on file."
                        />
                        <p className="text-xs text-muted-foreground">
                          Rejecting flips the registration back to non-member and re-applies
                          the rodeo's non-member surcharge.
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 justify-end">
                      {isAction ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setPendingAction(null);
                              setRejectReason('');
                            }}
                          >
                            Cancel
                          </Button>
                          {pendingAction!.mode === 'verify' ? (
                            <Button
                              size="sm"
                              disabled={verify.isPending}
                              onClick={() => handleVerify(target)}
                            >
                              {verify.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 mr-2" />
                              )}
                              Confirm verify
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={reject.isPending}
                              onClick={() => handleReject(target)}
                            >
                              {reject.isPending ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <X className="h-4 w-4 mr-2" />
                              )}
                              Confirm reject
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPendingAction({
                                registration_id: target.registration_id,
                                mode: 'reject',
                              })
                            }
                          >
                            <X className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={() =>
                              setPendingAction({
                                registration_id: target.registration_id,
                                mode: 'verify',
                              })
                            }
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Verify
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
